import type { ReactNode } from 'react';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import type {
  QuoteRequest,
  QuoteSubmission,
} from '@/util/types/serverTypeCollection/repairList';
import { QWIKFIX_BRAND } from '@/app/client/reports/components/quickfix-repair-quote/qwikfixBrand';

export type UnifiedQuoteEntry =
  | { type: 'request'; id: string; sortDate: number; quote: QuoteRequest }
  | { type: 'submission'; id: string; sortDate: number; submission: QuoteSubmission };

export type QuoteActionKey =
  | 'view-shareable-list'
  | 'view-in-qwikfix'
  | 'view-in-thumbtack'
  | 'view-quote'
  | 'repair-pdf';

export type QuoteActionItem = {
  key: QuoteActionKey;
  label: string;
  startContent: ReactNode;
};

export function buildUnifiedQuoteEntries({
  quoteRequests = [],
  quoteSubmissions = [],
}: {
  quoteRequests?: QuoteRequest[];
  quoteSubmissions?: QuoteSubmission[];
}): UnifiedQuoteEntry[] {
  const entries: UnifiedQuoteEntry[] = [];

  for (const quote of quoteRequests) {
    const sortDate =
      quote.status === 'received' && quote.quotedAt
        ? new Date(quote.quotedAt).getTime()
        : new Date(quote.requestedAt).getTime();
    entries.push({
      type: 'request',
      id: quote._id || `request-${String(quote.requestedAt)}`,
      sortDate,
      quote,
    });
  }

  for (const submission of quoteSubmissions) {
    entries.push({
      type: 'submission',
      id: submission._id || `submission-${String(submission.submittedAt)}`,
      sortDate: new Date(submission.submittedAt).getTime(),
      submission,
    });
  }

  return entries.sort((a, b) => {
    const aReceived = a.type === 'request' && a.quote.status === 'received';
    const bReceived = b.type === 'request' && b.quote.status === 'received';
    if (aReceived && !bReceived) return -1;
    if (!aReceived && bReceived) return 1;
    return b.sortDate - a.sortDate;
  });
}

export function getQuoteEntryItemCount(entry: UnifiedQuoteEntry): number {
  if (entry.type === 'request') return entry.quote.repairItemIds?.length || 0;
  return entry.submission.includedItemIds?.length || 0;
}

export const formatQuoteEntryDate = (date?: Date | string) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatQuoteCurrency = (amount?: number) => {
  if (!amount) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const getQuoteRequestStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'received':
      return 'success';
    case 'expired':
      return 'danger';
    default:
      return 'default';
  }
};

export const formatSubmissionType = (type: string) => {
  switch (type) {
    case 'thumbtack':
      return 'Thumbtack';
    case 'qwikfix':
      return 'QwikFix';
    case 'partner':
      return 'Partner';
    case 'other':
      return 'Other';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

export const buildThumbtackResultsUrl = (requestPk: string) =>
  `https://www.thumbtack.com/search/results/${requestPk}/`;

export function getSubmissionPdfUrl(submission: QuoteSubmission): string | undefined {
  return submission.providerMetadata?.repairRequestPdfUrl;
}

export function buildQuoteActionItems({
  pdfUrl,
  quoteResponseUrl,
  showQuoteResponse,
  isQwikFix,
  dashboardUrl,
  thumbtackRequestPk,
}: {
  pdfUrl?: string;
  quoteResponseUrl?: string;
  showQuoteResponse?: boolean;
  isQwikFix?: boolean;
  dashboardUrl?: string;
  thumbtackRequestPk?: string;
}): QuoteActionItem[] {
  const items: QuoteActionItem[] = [];

  if (isQwikFix && dashboardUrl) {
    items.push({
      key: 'view-in-qwikfix',
      label: 'View in QwikFix',
      startContent: (
        <FaArrowUpRightFromSquare size={12} style={{ color: QWIKFIX_BRAND.cyanDeep }} />
      ),
    });
  }

  if (thumbtackRequestPk) {
    items.push({
      key: 'view-in-thumbtack',
      label: 'View in Thumbtack',
      startContent: (
        <svg
          aria-label='Thumbtack logo'
          width={14}
          height={14}
          viewBox='0 0 16 16'
          fill='currentColor'
          className='block shrink-0'
        >
          <path d='M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0' />
          <path
            fill='#FFF'
            d='M8.973 10.385a3.71 3.71 0 0 1-.564 1.957L8 13l-.409-.658a3.704 3.704 0 0 1-.564-1.957v-3.14C7.51 6.62 8.231 6.4 8.973 6.4v3.985zM4 5.69V4h8v1.69H4z'
          />
        </svg>
      ),
    });
  }

  if (showQuoteResponse && quoteResponseUrl) {
    items.push({
      key: 'view-quote',
      label: 'View Quote',
      startContent: <FaArrowUpRightFromSquare size={12} className='text-success' />,
    });
  }

  items.push({
    key: 'view-shareable-list',
    label: 'View Shareable List',
    startContent: <FaArrowUpRightFromSquare size={12} />,
  });

  if (pdfUrl) {
    items.push({
      key: 'repair-pdf',
      label: 'Repair PDF',
      startContent: <FaArrowUpRightFromSquare size={12} />,
    });
  }

  return items;
}

export function handleQuoteEntryAction({
  key,
  itemIds,
  pdfUrl,
  quoteResponseUrl,
  dashboardUrl,
  thumbtackRequestPk,
  buildRepairListViewUrl,
  repairListId,
}: {
  key: QuoteActionKey;
  itemIds: string[];
  pdfUrl?: string;
  quoteResponseUrl?: string;
  dashboardUrl?: string;
  thumbtackRequestPk?: string;
  buildRepairListViewUrl: (repairListId: string, itemIds: string[]) => string;
  repairListId: string;
}) {
  if (key === 'view-shareable-list') {
    const url = buildRepairListViewUrl(repairListId, itemIds);
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'view-in-qwikfix' && dashboardUrl) {
    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'view-in-thumbtack' && thumbtackRequestPk) {
    window.open(buildThumbtackResultsUrl(thumbtackRequestPk), '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'view-quote' && quoteResponseUrl) {
    window.open(quoteResponseUrl, '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'repair-pdf' && pdfUrl) {
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }
}
